import { inject, Injectable, OnDestroy } from '@angular/core';
import { CredentialsService } from '@app/core';
import { UsersBrands } from '@app/core/services/api/API.service';
import {
    AmplifyApiService,
    UsersBrandsService as UsersBrandsApiService
} from '@app/core/api/services';
import { ROLES_GROUPS } from '@app/constants';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { sortJSON, UtilService } from '../../util.service';
import { take } from 'rxjs/operators';
import * as subscriptions from '@app/core/graphql/subscriptions';

@Injectable({
    providedIn: 'root'
})
export class UsersBrandsPreloadService implements OnDestroy {
    private _usersBrandsApiService = inject(UsersBrandsApiService);
    private _amplifyApiService = inject(AmplifyApiService);
    private _credentialsService = inject(CredentialsService);
    private _usersBrands$ = new BehaviorSubject<UsersBrands[]>([]);
    private _loading$ = new BehaviorSubject<boolean>(true);
    private usersBrands: UsersBrands[] = [];
    private _isLoadingUsersBrands = false;
    private _loadedScope: string | null = null;
    private subscriptions: any[] = [];

    constructor(private _utilService: UtilService) {
        this.subscriptions.push(
            this._amplifyApiService
                .subscribe({ query: subscriptions.onCreateUsersBrands })
                .subscribe(observer => {
                    const data = observer.data
                        .onCreateUsersBrands as UsersBrands;
                    if (!this.belongsToCurrentScope(data)) {
                        return;
                    }
                    this.usersBrands.push(data);
                    this.publishUsersBrands();
                }),
            this._amplifyApiService
                .subscribe({ query: subscriptions.onUpdateUsersBrands })
                .subscribe(observer => {
                    const data = observer.data
                        .onUpdateUsersBrands as UsersBrands;
                    const index = this.usersBrands.findIndex(
                        usersBrand => usersBrand.id === data.id
                    );
                    if (this.belongsToCurrentScope(data)) {
                        if (index === -1) {
                            this.usersBrands.push(data);
                        } else {
                            this.usersBrands[index] = data;
                        }
                    } else if (index !== -1) {
                        this.usersBrands.splice(index, 1);
                    }
                    this.publishUsersBrands();
                }),
            this._amplifyApiService
                .subscribe({ query: subscriptions.onDeleteUsersBrands })
                .subscribe(observer => {
                    const data = observer.data.onDeleteUsersBrands;
                    this.usersBrands = this.usersBrands.filter(
                        usersBrand => usersBrand.id !== data.id
                    );
                    this._usersBrands$.next(this.usersBrands);
                })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    get usersBrands$() {
        return this._usersBrands$.asObservable();
    }

    get loading$() {
        return this._loading$.asObservable();
    }

    async listUsersBrands(forceRefresh: boolean = false) {
        if (this._isLoadingUsersBrands && !forceRefresh) {
            return;
        }

        const scopeKey = this.resolveScopeKey();
        const existingUsersBrands = this._usersBrands$.getValue();
        if (
            !forceRefresh &&
            existingUsersBrands?.length > 0 &&
            this._loadedScope === scopeKey
        ) {
            return;
        }

        if (forceRefresh) {
            this._isLoadingUsersBrands = false;
        }

        this._isLoadingUsersBrands = true;
        try {
            this._loading$.next(true);
            this.usersBrands = await this.fetchUsersBrandsForCurrentUser();
            this._loadedScope = scopeKey;
        } catch (err) {
            console.error(err);
            this.usersBrands = [];
            this._loadedScope = null;
        } finally {
            this.publishUsersBrands();
            this._loading$.next(false);
            this._isLoadingUsersBrands = false;
        }
    }

    async findUsersBrandById(id: string): Promise<UsersBrands | undefined> {
        try {
            const usersBrands = await firstValueFrom(
                this.usersBrands$.pipe(take(1))
            );
            return usersBrands.find(usersBrand => usersBrand.id === id);
        } catch (error) {
            console.error('Error al buscar la marca de usuario:', error);
            return undefined;
        }
    }

    async findUsersBrandByClientId(clientId: string): Promise<UsersBrands[]> {
        try {
            const usersBrands = await firstValueFrom(
                this.usersBrands$.pipe(take(1))
            );
            return usersBrands.filter(
                usersBrand => usersBrand.clientId === clientId
            );
        } catch (error) {
            console.error(
                'Error al buscar marcas de usuario por cliente:',
                error
            );
            return [];
        }
    }

    private resolveScopeKey(): string | null {
        const user = this._credentialsService.credentialsCurrentUser;
        if (!user) {
            return null;
        }
        if (ROLES_GROUPS.SD_GROUP.includes(user.role)) {
            return `sd:${user.id}`;
        }
        if (ROLES_GROUPS.MKT_GROUP.includes(user.role)) {
            return `mkt:${user.clientId ?? user.id}`;
        }
        return `other:${user.id}`;
    }

    private belongsToCurrentScope(usersBrand: UsersBrands): boolean {
        const user = this._credentialsService.credentialsCurrentUser;
        if (!user) {
            return false;
        }
        if (ROLES_GROUPS.SD_GROUP.includes(user.role)) {
            return true;
        }
        if (ROLES_GROUPS.MKT_GROUP.includes(user.role)) {
            return usersBrand.clientId === user.clientId;
        }
        return false;
    }

    private async fetchUsersBrandsForCurrentUser(): Promise<UsersBrands[]> {
        const user = this._credentialsService.credentialsCurrentUser;
        if (!user) {
            return [];
        }

        if (ROLES_GROUPS.SD_GROUP.includes(user.role)) {
            return (await this._usersBrandsApiService.listUsersBrands()) ?? [];
        }

        if (ROLES_GROUPS.MKT_GROUP.includes(user.role) && user.clientId) {
            return (
                (await this._usersBrandsApiService.getUsersBrandByClientId(
                    user.clientId
                )) ?? []
            );
        }

        return [];
    }

    private publishUsersBrands(): void {
        this.usersBrands = sortJSON(this.usersBrands, 'name', 'asc');
        this._usersBrands$.next(this.usersBrands);
    }
}
